<template>
  <div
    role="button"
    class="back-to-top-button"
    :class="cssClasses"
    :aria-label="t('back_to_top_button_aria_label')"
    @click="handleScrollButtonClick"
  >
    <slot name="content" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import ScrollUtils from "@utils/scroll.utils";
import DOMUtils from "@utils/dom.utils";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant";

// Props
interface Props {
  isScrollBottom?: boolean;
  container?: string;
}

const { isScrollBottom = false, container = "" } = defineProps<Props>();

// Composables
const { t } = useI18n();
const store = useStore();

// Computed properties
const isTeacherView = computed(() => {
  // TODO: Anti pattern usage of attempt store is not for generalize component, should pass as prop
  if (NAMESPACE.ATTEMPT && store.getters[`${NAMESPACE.ATTEMPT}/isTeacherView`]) {
    return store.getters[`${NAMESPACE.ATTEMPT}/isTeacherView`];
  }
  return false;
});

const cssClasses = computed(() => [
  {
    "teachers-view": isTeacherView.value,
  },
]);

// Methods
const handleScrollButtonClick = () => {
  if (!isScrollBottom) {
    ScrollUtils.windowScrollTop();
  } else {
    ScrollUtils.scrollToBottomOfContainer(DOMUtils.el(container));
  }
};
</script>

<style lang="scss">
@use "carbon-components/scss/globals/scss/motion.scss" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/abstracts/mixins" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.back-to-top-button {
  @include carbon--type-style("button-02");
  position: fixed;
  height: 48px;
  width: 48px;
  right: 0.5rem;
  bottom: 0.5rem;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  background-color: $inverse-link-01;
  color: $active-tertiary;
  border-radius: 50%;
  box-shadow: $shadow-02;
  opacity: 0.7;
  z-index: calc(#{z-map("page-nav")} + 1);
  cursor: pointer;

  animation: fadeInUp ease-in-out 1s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;

  &:hover {
    opacity: 1;
  }

  &.conversation {
    position: absolute;
    width: 40px;
    height: 40px;
    bottom: 1rem;
    right: 1.5rem;
    animation: fadeInDown ease-in-out 1s;

    .sls-icon {
      [fill] {
        fill: $active-tertiary;
      }
    }
  }

  &.teachers-view {
    background-color: $inverse-02;
    color: $ui-01;
    .sls-icon {
      [fill] {
        fill: $ui-01;
      }
    }
  }

  span {
    display: inline-flex;
    max-height: 14px;
    align-items: center;
  }

  .sls-icon {
    [fill] {
      fill: $active-tertiary;
    }
  }

  @keyframes fadeInUp {
    0% {
      opacity: 0;
      transform: translateY(50%);
    }
    100% {
      transform: translateY(0);
    }
  }

  @keyframes fadeInDown {
    0% {
      opacity: 0;
      transform: translateY(-50%);
    }
    100% {
      transform: translateY(0);
    }
  }
}
</style>
